import { useEffect, useRef, type ReactNode } from 'react';
import { BADGE_PALETTE } from './badges';
import { drawSprite, type Sprite } from './sprites';

interface Props {
  readonly badge: Sprite;
  readonly className?: string | undefined;
  /** Vignette éteinte : le succès n'est pas encore tombé. */
  readonly locked?: boolean | undefined;
}

/**
 * La vignette d'un succès, dans un canvas à sa taille logique.
 *
 * Elle ne passe pas par `PixelSprite` pour une seule raison : sa palette n'est
 * pas celle du jeu. Un succès verrouillé est peint dans une palette entièrement
 * grise plutôt que par un filtre CSS — `filter: grayscale()` force une couche
 * de composition supplémentaire par vignette, pour un résultat identique.
 */
export function AwardBadge({ badge, className, locked = false }: Props): ReactNode {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const context = ref.current?.getContext('2d');
    if (!context) return;
    context.imageSmoothingEnabled = false;
    context.clearRect(0, 0, badge.width, badge.height);
    const palette = locked
      ? Object.fromEntries(Object.keys(BADGE_PALETTE).map((key) => [key, '#2a2a4a']))
      : BADGE_PALETTE;
    drawSprite(context, badge, 0, 0, 1, palette);
  }, [badge, locked]);

  return (
    <canvas ref={ref} className={className} width={badge.width} height={badge.height} aria-hidden="true" />
  );
}
